import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getOrganizationMutationKey } from '.';
import { organizationService } from '../../application/OrganizationService';
import { OrganizationActionType } from '../../context/organization/actions';
import { useOrganizationContext } from '../../context/organization/context';
import { useUserContext } from '../../context/user/context';
import { NumOfVehiclesTypes, Organization } from '../../domain/organization/Organization';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseUpdateOrganizationOnboardingProps {
  label?: string | null;
  numOfVehicles?: NumOfVehiclesTypes;
  goal?: string[];
  section?: string;
  active?: boolean;
}

type UseUpdateOrganizationOnboardingResponse = ServiceResponse<
  (props: UseUpdateOrganizationOnboardingProps) => void,
  Organization
>;

// Hooks act as 'Adapter' layer.
export const useUpdateOrganizationOnboarding = (): UseUpdateOrganizationOnboardingResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const queryClient = useQueryClient();

  const { userState } = useUserContext();
  const { organizationState, organizationDispatch } = useOrganizationContext();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ label, numOfVehicles, goal, section, active }: UseUpdateOrganizationOnboardingProps): Promise<Organization> => {
      organizationState.organization!.update({
        label,
        num_of_vehicles: numOfVehicles,
        goal,
        section,
        active,
      });
      return organizationService.saveOnboarding({
        user: userState.user!,
        language: userState.language,
        organization: organizationState.organization!,
      });
    },
    // TODO: OPTIMISTIC UPDATE!
    {
      onSuccess: (updatedOrganization) => {
        organizationDispatch({
          type: OrganizationActionType.UPDATE_ORGANIZATION_SUCCESS,
          payload: { organization: updatedOrganization },
        });
        queryClient.invalidateQueries(getOrganizationMutationKey());
      },
      onError: (err: any) => {
        console.info(`Update Organization Onboarding Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: ({ label, numOfVehicles, goal, section, active }: UseUpdateOrganizationOnboardingProps) =>
      mutate({
        label,
        numOfVehicles,
        goal,
        section,
        active,
      }),
    data: organizationState.organization,
  };
};
